import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Check, X, ClipboardList, RefreshCw, Repeat } from 'lucide-react';
import { aiAssistantService, type TradeProposal } from '@/services/aiAssistantService';

/**
 * Pending AI trade proposals. In 'propose only' trade-authority mode the
 * assistant queues trades here for the user to approve or reject.
 */
export const TradeProposalsPanel: React.FC = () => {
  const [proposals, setProposals] = useState<TradeProposal[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-proposal in-flight set — a single busy id would let a click on one
  // proposal re-enable another row mid-request and allow a double-submit
  // (and a duplicate approve can place a duplicate broker order).
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setProposals(await aiAssistantService.listProposals());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const decide = async (id: string, approve: boolean) => {
    if (busyIds.has(id)) return;   // already in flight — ignore the repeat click
    setBusyIds((s) => new Set(s).add(id));
    setError(null);
    try {
      const result = approve
        ? await aiAssistantService.approveProposal(id)
        : await aiAssistantService.rejectProposal(id);
      // Drop it from the pending list; surface a failed execution.
      setProposals((p) => p.filter((x) => x.id !== id));
      if (approve && result.status === 'Failed')
        setError(`Trade not placed: ${result.resultMessage ?? 'unknown error'}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Action failed';
      // A 409 means another client already decided this proposal — it can
      // never succeed now, so drop the stale row instead of looping on errors.
      if (/already/i.test(msg)) setProposals((p) => p.filter((x) => x.id !== id));
      setError(msg);
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  // Nothing pending and loaded cleanly — keep the page uncluttered.
  if (!loading && proposals.length === 0 && !error) return null;

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ClipboardList className="h-4 w-4 text-primary" />
          Trade proposals awaiting approval
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {proposals.map((p) => (
            <div key={p.id} className="rounded-md border p-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">
                  {p.kind === 'Close'
                    ? `Close ${p.symbol}`
                    : `${p.side === 'sell' ? 'Sell' : 'Buy'} ${p.quantity} ${p.symbol}`}
                </span>
                <div className="flex gap-1.5">
                  <Button size="sm" className="h-7 px-2" disabled={busyIds.has(p.id)}
                          onClick={() => void decide(p.id, true)}>
                    {busyIds.has(p.id)
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <><Check className="h-3.5 w-3.5 mr-1" /> Approve</>}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2" disabled={busyIds.has(p.id)}
                          onClick={() => void decide(p.id, false)}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
              {p.rationale && (
                <p className="mt-1 text-xs text-muted-foreground">{p.rationale}</p>
              )}
              {(p.scheduleId || p.conversationId) && (
                <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  {p.scheduleId && (
                    <span className="inline-flex items-center gap-1">
                      <Repeat className="h-3 w-3" /> Triggered by scheduled run
                    </span>
                  )}
                  {p.conversationId && (
                    <a
                      href={`#/dashboard/ai-assistant?conversation=${encodeURIComponent(p.conversationId)}`}
                      className="inline-flex items-center gap-1 underline hover:text-foreground">
                      View conversation
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default TradeProposalsPanel;
